import type { TicketingCategory } from '@cpce/shared';
import type { SubsidyProduct } from '@cpce/ticketing-api';
import { useId, useState } from 'react';
import { Checkbox } from '@/components/Checkbox';
import { Combobox, type ComboboxOptions } from '@/components/Combobox';
import { formatDate } from '@/utils/date.utils';

export type SubsidyTargetValue = {
  ticketingCategoryId: number | null;
  ticketingProductVariantCode: string | null;
};

const PRODUCT_PREFIX = 'product:';
const CATEGORY_PREFIX = 'category:';
const NO_TARGET = 'none';

export const formatSubsidyProductLabel = (label: string) => `Produit · ${label}`;

/** How a prestation's subsidy target reads in a list: its product rule, else its category path. */
export const subsidyTargetLabel = (prestation: {
  ticketingProductLabel: string | null;
  category: { path: string } | null;
}): string | null =>
  prestation.ticketingProductLabel
    ? formatSubsidyProductLabel(prestation.ticketingProductLabel)
    : (prestation.category?.path ?? null);

const formatPeriod = ({ dateDebut, dateFin }: SubsidyProduct): string => {
  if (!dateDebut && !dateFin) return '';
  const format = (value: string | null) => (value ? formatDate(new Date(value)) : '…');
  return ` (${format(dateDebut)} → ${format(dateFin)})`;
};

interface SubsidyTargetComboboxProps {
  categories: TicketingCategory[];
  /** The categories a rule of the CSE subsidises; null when the rules could not be read. */
  ruledCategoryIds: ReadonlySet<number> | null;
  products: SubsidyProduct[];
  value: SubsidyTargetValue;
  /** Label saved on the prestation, for a product whose rule is no longer listed. */
  savedProductLabel: string | null;
  /** Lets the target be cleared, under this label: an adhésion's subsidy is optional. */
  noTargetLabel?: string;
  onChange: (value: SubsidyTargetValue) => void;
  disabled?: boolean;
  error?: string;
  isLoading?: boolean;
}

export function SubsidyTargetCombobox({
  categories,
  ruledCategoryIds,
  products,
  value,
  savedProductLabel,
  noTargetLabel,
  onChange,
  disabled,
  error,
  isLoading,
}: SubsidyTargetComboboxProps) {
  const checkboxId = useId();
  const [showsUnruled, setShowsUnruled] = useState(false);
  const selectedVariant = value.ticketingProductVariantCode;
  const isRuled = (category: TicketingCategory) => ruledCategoryIds?.has(category.id) ?? true;
  const categoryOptions: ComboboxOptions<string> = categories
    .filter((c) => showsUnruled || isRuled(c) || c.id === value.ticketingCategoryId)
    .map((c) => ({
      value: `${CATEGORY_PREFIX}${c.id}`,
      label: c.path,
      badge: isLoading || isRuled(c) ? undefined : 'Sans règle',
    }));
  const productOptions: ComboboxOptions<string> = products.map((p) => ({
    value: `${PRODUCT_PREFIX}${p.variantCode}`,
    label: formatSubsidyProductLabel(`${p.label}${formatPeriod(p)}`),
  }));
  const isSelectedListed = products.some((p) => p.variantCode === selectedVariant);
  const options: ComboboxOptions<string> = [
    ...(noTargetLabel ? [{ value: NO_TARGET, label: noTargetLabel }] : []),
    ...categoryOptions,
    ...productOptions,
    ...(selectedVariant && !isSelectedListed
      ? [
          {
            value: `${PRODUCT_PREFIX}${selectedVariant}`,
            label: formatSubsidyProductLabel(savedProductLabel ?? selectedVariant),
          },
        ]
      : []),
  ];

  const selected = selectedVariant
    ? `${PRODUCT_PREFIX}${selectedVariant}`
    : value.ticketingCategoryId
      ? `${CATEGORY_PREFIX}${value.ticketingCategoryId}`
      : noTargetLabel
        ? NO_TARGET
        : undefined;

  const handleChange = (option: string) => {
    if (option === NO_TARGET) {
      onChange({ ticketingCategoryId: null, ticketingProductVariantCode: null });
      return;
    }
    if (option.startsWith(PRODUCT_PREFIX)) {
      onChange({
        ticketingCategoryId: null,
        ticketingProductVariantCode: option.slice(PRODUCT_PREFIX.length),
      });
      return;
    }
    onChange({
      ticketingCategoryId: Number(option.slice(CATEGORY_PREFIX.length)),
      ticketingProductVariantCode: null,
    });
  };

  return (
    <div className="flex flex-col gap-2">
      <Combobox
        value={selected}
        onChange={handleChange}
        options={options}
        disabled={disabled}
        error={error}
        isLoading={isLoading}
        emptyMessage={
          showsUnruled || ruledCategoryIds === null
            ? undefined
            : "Aucune catégorie n'a de règle de subvention : cochez « Afficher les catégories sans règle »."
        }
      />
      {ruledCategoryIds === null ? (
        <span className="text-xs text-gray-500">
          Règles de subvention indisponibles : toutes les catégories sont affichées.
        </span>
      ) : (
        !disabled && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Checkbox
              id={checkboxId}
              checked={showsUnruled}
              onCheckedChange={(checked) => setShowsUnruled(checked === true)}
            />
            <label htmlFor={checkboxId}>Afficher les catégories sans règle</label>
          </div>
        )
      )}
    </div>
  );
}
